import {
  experienceFeedbackDetailAtom,
  experienceFeedbackMarkSubmittedAtom,
  experienceFeedbackRatingAtom,
  experienceFeedbackRatingSubmittedAtom,
  experienceFeedbackResetFormAtom,
  experienceFeedbackSnoozeCountAtom,
  experienceFeedbackUsefulnessRatingAtom,
} from '@/atoms/experienceFeedbackAtoms';
import { submitEvidenceFeedback } from '@/components/chat/api/evidenceFeedbackApi';
import { toast } from '@heidi/design-system/toast';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type Dispatch } from '@/store';
import { CSATRating } from '@heidi/schema/ml-scribe';
import { logger } from '@/utils/logger/logger';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { track } from '@/lib/analytics/track';

interface UseExperienceFeedbackSubmissionOptions {
  /**
   * Number of completed queries at the time of submission.
   * Used for analytics.
   */
  queryCount: number;
  /**
   * The chat ID where feedback is being given.
   * Used for analytics correlation.
   */
  chatId?: string;
  /**
   * The session ID if in session.
   * Used for analytics correlation.
   */
  sessionId?: string;
}

interface UseExperienceFeedbackSubmissionResult {
  /**
   * Ease rating (1-5) or null if not selected.
   */
  easeRating: CSATRating | null;

  /**
   * Usefulness rating (1-5) or null if not selected.
   */
  usefulnessRating: CSATRating | null;

  /**
   * Current detail/comment value.
   */
  detail: string;

  /**
   * Whether the ease rating has been submitted (Phase 2 of the two-phase flow).
   */
  isRatingSubmitted: boolean;

  /**
   * Set ease rating and transition to Phase 2. Tracks to analytics.
   */
  submitEaseRating: (rating: CSATRating) => void;

  /**
   * Set usefulness rating. Tracks to analytics when in Phase 2.
   */
  submitUsefulnessRating: (rating: CSATRating) => void;

  /**
   * Set the detail/comment value.
   */
  setDetail: (detail: string) => void;

  /**
   * Submit full feedback (ease + usefulness + optional comment). Closes nudge.
   */
  submitFeedback: () => Promise<void>;

  /**
   * Whether submit button should be disabled (both ratings required).
   */
  isSubmitDisabled: boolean;

  /**
   * Whether the feedback is currently being submitted to the backend.
   */
  isSubmitting: boolean;
}

/**
 * Hook to manage the submission of experience feedback (UMUX-Lite).
 *
 * Two-phase flow:
 * 1. User selects ease star → Tracked to analytics, Phase 2 shown (usefulness + comment).
 * 2. User selects usefulness and optionally comment, clicks Submit → Full feedback submitted.
 *
 * @see docs/design-docs/experience-feedback/README.md
 */
export const useExperienceFeedbackSubmission = ({
  queryCount,
  chatId,
  sessionId,
}: UseExperienceFeedbackSubmissionOptions): UseExperienceFeedbackSubmissionResult => {
  const { t } = useTranslations();
  const dispatch = useDispatch<Dispatch>();
  const { baseProps } = useEvidenceAnalytics();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Atom values
  const easeRating = useAtomValue(experienceFeedbackRatingAtom);
  const usefulnessRating = useAtomValue(experienceFeedbackUsefulnessRatingAtom);
  const detail = useAtomValue(experienceFeedbackDetailAtom);
  const snoozeCount = useAtomValue(experienceFeedbackSnoozeCountAtom);
  const isRatingSubmitted = useAtomValue(experienceFeedbackRatingSubmittedAtom);

  // Atom setters
  const setEaseRatingAtom = useSetAtom(experienceFeedbackRatingAtom);
  const setUsefulnessRatingAtom = useSetAtom(experienceFeedbackUsefulnessRatingAtom);
  const setDetailAtom = useSetAtom(experienceFeedbackDetailAtom);
  const setRatingSubmitted = useSetAtom(experienceFeedbackRatingSubmittedAtom);
  const markSubmitted = useSetAtom(experienceFeedbackMarkSubmittedAtom);
  const resetForm = useSetAtom(experienceFeedbackResetFormAtom);

  /**
   * Phase 1: Set ease rating and transition to Phase 2. Tracks to analytics.
   */
  const submitEaseRating = useCallback(
    (newRating: CSATRating) => {
      setEaseRatingAtom(newRating);
      track({ options: { amplitude: false },
        event_name: TrackingEventName.EvidenceExperienceFeedbackRatingSubmitted,
        props: {
          ...baseProps,
          rating_type: 'ease',
          rating: newRating,
          query_count: queryCount,
          snooze_count: snoozeCount,
          chat_id: chatId,
          session_id: sessionId,
        },
      });
      setRatingSubmitted(true);
    },
    [
      dispatch,
      baseProps,
      queryCount,
      snoozeCount,
      chatId,
      sessionId,
      setEaseRatingAtom,
      setRatingSubmitted,
    ]
  );

  /**
   * Phase 2: Set usefulness rating. Tracks to analytics.
   */
  const submitUsefulnessRating = useCallback(
    (newRating: CSATRating) => {
      setUsefulnessRatingAtom(newRating);
      track({ options: { amplitude: false },
        event_name: TrackingEventName.EvidenceExperienceFeedbackRatingSubmitted,
        props: {
          ...baseProps,
          rating_type: 'usefulness',
          rating: newRating,
          query_count: queryCount,
          snooze_count: snoozeCount,
          chat_id: chatId,
          session_id: sessionId,
        },
      });
    },
    [
      dispatch,
      baseProps,
      queryCount,
      snoozeCount,
      chatId,
      sessionId,
      setUsefulnessRatingAtom,
    ]
  );

  const setDetail = useCallback(
    (newDetail: string) => {
      setDetailAtom(newDetail);
    },
    [setDetailAtom]
  );

  /**
   * Phase 2: Submit full feedback (ease + usefulness + optional comment).
   */
  const submitFeedback = useCallback(async () => {
    if (easeRating === null || usefulnessRating === null) {
      return;
    }

    const trimmedComment = (detail ?? '').trim();
    const hasComment = trimmedComment.length > 0;

    setIsSubmitting(true);

    try {
      await submitEvidenceFeedback({
        ease_stars: easeRating as number,
        usefulness_stars: usefulnessRating as number,
        feedback: hasComment ? trimmedComment : undefined,
      });
      track({ options: { amplitude: false },
        event_name: TrackingEventName.EvidenceExperienceFeedbackSubmitted,
        props: {
          ...baseProps,
          ease_rating: easeRating as number,
          usefulness_rating: usefulnessRating as number,
          has_comment: hasComment,
          comment_length: trimmedComment.length,
          comment: trimmedComment,
          query_count_at_trigger: queryCount,
          snooze_count_before_submit: snoozeCount,
          chat_id: chatId,
          session_id: sessionId,
        },
      });
      markSubmitted();
      resetForm();
      toast({
        tone: 'success',
        title: t('feedback.experienceFeedback.thankYou'),
      });
    } catch (error) {
      logger.error('Failed to submit experience feedback:', error);
      track({ options: { amplitude: false },
        event_name: TrackingEventName.EvidenceExperienceFeedbackSubmitted,
        props: {
          ...baseProps,
          ease_rating: easeRating as number,
          usefulness_rating: usefulnessRating as number,
          has_comment: hasComment,
          comment_length: trimmedComment.length,
          comment: trimmedComment,
          query_count_at_trigger: queryCount,
          snooze_count_before_submit: snoozeCount,
          error: true,
          chat_id: chatId,
          session_id: sessionId,
        },
      });
      toast({
        tone: 'destructive',
        title: t('feedback.experienceFeedback.errorTitle'),
        description: t('feedback.experienceFeedback.errorDescription'),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    easeRating,
    usefulnessRating,
    detail,
    dispatch,
    baseProps,
    queryCount,
    snoozeCount,
    chatId,
    sessionId,
    markSubmitted,
    resetForm,
    t,
  ]);

  return {
    easeRating,
    usefulnessRating,
    detail,
    isRatingSubmitted,
    submitEaseRating,
    submitUsefulnessRating,
    setDetail,
    submitFeedback,
    isSubmitDisabled: easeRating === null || usefulnessRating === null,
    isSubmitting,
  };
};
